import { ContactAPI } from '@business/api/contact';
import { SettingsAPI } from '@business/api/settings';
import { Blocs } from '@business/blocs';
import { IntegrationType } from '@business/entities/integration';
import {
  RCCallActive,
  RCCallEnd,
  RCCallStart,
  RCDirection,
  RCEvent,
  RCEventType,
  RCMessageUpdate,
  RCPopupWindow,
} from '@business/entities/ringcentral';
import { Config } from '@config';
import { BaseBloc } from '@core/utils/bloc';
import { resolve } from '@core/utils/ioc';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { openCallNotesModal } from '@modules/common/CallNotesModal';
import { BehaviorSubject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ContactActivityType } from '@business/entities/contact';
import { Session } from '@modules/auth/session';
import { UserService } from '@business/services';

export class RingCentralBloc extends BaseBloc {
  isRCMinimized$ = new BehaviorSubject<boolean>(false);
  isActivated$ = new BehaviorSubject<boolean>(false);

  addScript() {
    const rcs = document.createElement('script');
    rcs.setAttribute('id', 'rc-script');
    const key = Config.BUILD_MODE === 'dev' ? 'MxGoj66mQTiIw4LA9pEbnQ' : 'zCjfx-LAROGfNx8wBVrXwg';
    const redirectUrl = Config.BUILD_MODE === 'dev' ? 'https://app.gc.local/ringcentral-redirect' : 'https://app.grandcentr.al/ringcentral-redirect';
    const platformURL = Config.BUILD_MODE === 'dev' ? 'https://platform.devtest.ringcentral.com' : 'https://platform.ringcentral.com';
    rcs.src = `/vendor/ringcentral/adapter.min2.js?clientId=${key}&appServer=${platformURL}&redirectUri=${redirectUrl}&zIndex=99999&disableGlip=true&multipleTabsSupport=1&disableMeeting=true&defaultDirection=left"`;
    // rcs.src = `https://ringcentral.github.io/ringcentral-embeddable-voice/adapter.js?clientId=${key}&appServer=${platformURL}&redirectUri=${redirectUrl}&zIndex=99999&disableGlip=true&multipleTabsSupport=1&disableMeeting=true&defaultDirection=left"`;
    const rcs0 = document.getElementsByTagName('script')[0];
    rcs0.parentNode!.insertBefore(rcs, rcs0);
    this.isActivated$.next(true);
  }

  removeScript() {
    const rcs = document.getElementById('rc-script');
    const rcw = document.getElementById('rc-widget');

    if (rcs) {
      rcs.parentNode!.removeChild(rcs);
    }
    if (rcw) {
      rcw.parentNode!.removeChild(rcw);
    }
    this.isActivated$.next(false);
  }

  onMessage = (event: RCEvent) => {
    switch (event.type) {
      case RCEventType.start: {
        const call = (event as RCCallStart).call;
        let phone = call.to;
        if (call.direction === RCDirection.in) {
          phone = call.from;
        }

        const context = (window as any).rcCallContext || {};

        if (document.visibilityState === 'visible' || call.direction === RCDirection.in) {
          ContactAPI.addCallLog(call.partyData.sessionId, phone, call.direction, context.objectId, context.objectType, context.contactId).subscribe();
        }

        ContactAPI.trackCall(
          call.partyData.sessionId,
          call.fromNumber || call.from,
          call.to,
          call.direction,
          call.startTime,
          call.startTime,
          context.objectId,
          context.objectType,
        ).subscribe();

        // Clear context after use
        try {
          delete (window as any).rcCallContext;
        } catch (e) {}

        break;
      }
      case RCEventType.active: {
        const call = (event as RCCallActive).call;

        let phone = call.to.phoneNumber;
        if (call.direction === RCDirection.in) {
          phone = call.from.phoneNumber;
        }

        return openCallNotesModal({ phoneNumber: phone });
      }
      case RCEventType.end: {
        const call = (event as RCCallEnd).call;

        const context = (window as any).rcCallContext || {};

        ContactAPI.trackCall(
          call.partyData.sessionId,
          call.fromNumber || call.from,
          call.to,
          call.direction,
          call.startTime,
          call.endTime,
          context.objectId,
          context.objectType,
        ).subscribe();

        // Clear context
        try {
          delete (window as any).rcCallContext;
        } catch (e) {}
        break;
      }
      case RCEventType.messageUpdate: {
        const message = (event as RCMessageUpdate).message;

        let phoneNumber = message.from.phoneNumber;
        if (message.direction === RCDirection.out) {
          phoneNumber = R.get(message, 'to[0].phoneNumber');
          if (!phoneNumber) {
            phoneNumber = R.get(message, 'to.phoneNumber');
          }
        }

        const context = (window as any).rcSmsContext || {};

        if (phoneNumber) {
          ContactAPI.getDataByPhone(phoneNumber)
            .pipe(
              switchMap(contact => {
                return resolve(Blocs.contactActivity).createActivity(
                  {
                    contact: contact.id,
                    notesResult: 'SMS - RingCentral',
                    content: message.subject,
                    forDate: toMoment().unix(),
                    activityType: ContactActivityType.leftMessage,
                    incoming: message.direction === RCDirection.in,
                    objectId: context.objectId,
                    objectType: context.objectType,
                  },
                  false,
                );
              }),
            )
            .subscribe();

          const toNumber = R.get(message, 'to[0].phoneNumber') || R.get(message, 'to.phoneNumber') || '';

          ContactAPI.trackSMS(
            message.from.phoneNumber || '',
            toNumber,
            message.direction,
            message.subject,
            context.objectId,
            context.objectType,
          ).subscribe();

          // Clear context after use
          try {
            delete (window as any).rcCallContext;
          } catch (e) {}
        }
        break;
      }
      case RCEventType.popupWindow: {
        this.isRCMinimized$.next((event as RCPopupWindow).minimized);
      }
    }
  };

  onInit() {
    SettingsAPI.fetchSettings(true)
      .pipe(
        tap(settings => {
          if (settings.integrations.find(i => i.type === IntegrationType.ringcentral)) {
            const user = Session.user;
            if (user && UserService.isStaff(user)) {
              this.addScript();
              (window as any).onRCMessage = this.onMessage;
            }
            // window.addEventListener('message', e => {
            //   this.onMessage(e.data);
            // });
          }
        }),
      )
      .subscribe();
  }

  onReset() {
    this.removeScript();
  }
}
